import React, { useState, useEffect, useRef } from "react";
import {
  EventsPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  StatsWrapper,
  ButtonWrapper,
  TitleWrapper,
  TotalEvents,
  TextDescription,
  EditAndDeleteContainer,
} from "./EventsPage.styles";
import Sidebar from "../../components/Sidebar";
import Table from "../../components/Table";
import { Toast } from "primereact/toast";
import Button from "../../components/Buttons/Button";
import IconButton from "../../components/Buttons/IconButton";
import { CreateModal, ConfirmModal } from "../../components/Modals";
import DeletedRow from "../../components/DeletedRow";
import RestoredRow from "../../components/RestoredRow";
import editIcon from "../../assets/icons/editIcon.svg";
import delIcon from "../../assets/icons/delIcon.svg";

import {
  APIResponse,
  CreateEventPayload,
  Event,
} from "../../services/eventService/eventService.types";
import {
  fetchEvents,
  createEvent,
  updateEvent,
  deleteEvent,
} from "../../services/eventService/eventService";


import { pxToRem } from "../../styles/utils";
import { Course } from "../../services/courseService/courseService.types";
import { fetchAllCourses } from "../../services/courseService/courseService";
import { useSubscription } from '../../hooks/useSubscription';
import { formatServerErrors } from "../../utils/errorHandler";

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [eventCount, setEventCount] = useState<number>(0);

  const [orderBy, setOrderBy] = useState<string>("");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toast = useRef<Toast>(null);
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();

  useEffect(() => {
    loadEvents();
  }, [currentPage, pageSize, orderBy]);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const list = await fetchAllCourses();
        setCourses(list);
      } catch (err) {
        console.error("Ошибка при загрузке курсов:", err);
      }
    };
    loadCourses();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: APIResponse<Event> = await fetchEvents(
        currentPage,
        pageSize,
        orderBy
      );
      const normalized = response.results.map(evt => ({
        ...evt,
        course_title: evt.course_title,
        course: evt.course,
      }));
      setEvents(normalized);
      setTotalPages(Math.ceil(response.total_events / pageSize));
      setEventCount(response.total_events);
    } catch (err) {
      console.error("Ошибка при загрузке событий:", err);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Ошибка загрузки событий",
        life: 3000,
      });
      setError("Не удалось загрузить список событий.");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrderBy(`-${field}`);
    } else if (orderBy === `-${field}`) {
      setOrderBy("");
    } else {
      setOrderBy(field);
    }
  };

  const handleCreateEvent = async (
    formData: Record<string, string | File[] | null>
  ) => {
    setExternalErrors({});
    if (!formData.course) {
      toast.current?.show({
        severity: "warn",
        summary: "Внимание",
        detail: "Выберите курс",
        life: 3000,
      });
      return;
    }
    const courseId = Number(formData.course as string);
    if (isNaN(courseId)) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Неверный ID курса",
        life: 3000,
      });
      return;
    }
    const payload = {
      course: courseId,
      calendar_day: formData.calendar_day as string,
      description: formData.description as string,
      time: formData.time as string,
    };
    try {
      const newEvent = await createEvent(payload as any);
      toast.current?.show({
        severity: "success",
        summary: "Успех",
        detail: "Событие добавлено",
        life: 3000,
      });
      closeCreateModal();
      setEvents((prev) => [newEvent, ...prev]);
      setEventCount((prev) => prev + 1);
      setRecentlyCreated(newEvent.id);
      setTimeout(() => setRecentlyCreated(null), 3000);
    } catch (error: any) {
      if (error.response && error.response.data) {
        const serverErrors = error.response.data;
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
        
        // Показываем toast только для общих ошибок
        if (formattedErrors.error || formattedErrors.non_field_errors) {
          toast.current?.show({
            severity: "error",
            summary: "Ошибка",
            detail: formattedErrors.error || formattedErrors.non_field_errors || "Не удалось создать событие",
            life: 3000,
          });
        }
      } else {
        console.error("Ошибка при создании события:", error);
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось создать событие",
          life: 3000,
        });
      }
    }
  };

  const handleEditEvent = async (
    formData: Record<string, string | File[] | null>
  ) => {
    if (!editingEvent) return;
    setExternalErrors({});
    if (!formData.course) {
      toast.current?.show({
        severity: "warn",
        summary: "Внимание",
        detail: "Выберите курс",
        life: 3000,
      });
      return;
    }
    const courseId = Number(formData.course as string);
    const payload = {
      course: courseId,
      calendar_day: formData.calendar_day as string,
      description: formData.description as string,
      time: formData.time as string,
    };
    console.log(payload);
    try {
      const updated = await updateEvent(editingEvent.id, payload as any);
      toast.current?.show({
        severity: "success",
        summary: "Успех",
        detail: "Событие обновлено",
        life: 3000,
      });
      setRecentlyEdited(editingEvent.id);
      setEvents((prev) =>
        prev.map((ev) => (ev.id === updated.id ? updated : ev))
      );
      closeEditModal();
      setTimeout(() => setRecentlyEdited(null), 3000);
    } catch (error: any) {
      if (error.response && error.response.data) {
        const serverErrors = error.response.data;
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
        
        // Показываем toast только для общих ошибок
        if (formattedErrors.error || formattedErrors.non_field_errors) {
          toast.current?.show({
            severity: "error",
            summary: "Ошибка",
            detail: formattedErrors.error || formattedErrors.non_field_errors || "Не удалось обновить событие",
            life: 3000,
          });
        }
      } else {
        console.error("Ошибка при обновлении события:", error);
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось обновить событие",
          life: 3000,
        });
      }
    }
  };

  const openEditModal = (event: Event) => {
    setEditingEvent(event);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingEvent(null);
  };
  const openCreateModal = () => setIsCreateModalOpen(true);
  const closeCreateModal = () => setIsCreateModalOpen(false);

  const openDeleteModal = (event: Event) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setEventToDelete(null);
  };
  const confirmDelete = () => {
    if (eventToDelete) {
      handleDelete(eventToDelete.id);
      closeDeleteModal();
    }
  };

  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteEvent(id);
        setPendingDelete(null);
        toast.current?.show({
          severity: "success",
          summary: "Успех",
          detail: "Событие удалено",
          life: 2000,
        });
        setEvents((prev) => prev.filter((ev) => ev.id !== id));
        setEventCount((prev) => prev - 1);
        loadEvents();
      } catch {
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить событие",
          life: 3000,
        });
        setPendingDelete(null);
      }
    }, 5000);
  };

  const restoreEventHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete;
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.current?.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление события отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  // Преобразуем список курсов для селекта
  const courseOptions = courses.map((c) => ({
    label: c.title,
    value: String(c.id),
  }));

  const headers = [
    { label: "Курс", key: "course", fieldName: "course__title", width: "50%" },
    { label: "Событие", key: "description", fieldName: "description" },
    { label: "Дата", key: "calendar_day", fieldName: "calendar_day__date" },
    { label: "Время", key: "time", fieldName: "time", sortable: false },
    {
      label: "",
      key: "editAndDelete",
      sortable: false,
      fieldName: "editAndDelete",
    },
  ];

  let rows = events.map((event) => {
    if (event.id === pendingDelete) {
      return {
        description: (
          <DeletedRow
            id={event.id}
            onRestore={restoreEventHandler}
            colSpan={headers.length}
            message={`Событие "${event.description}" будет удалено через 5 секунд.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    return {
      course: event.course_title,
      description: event.description,
      calendar_day: event.calendar_day,
      time: event.time,
      editAndDelete: hasActiveSubscription ? (
        <EditAndDeleteContainer>
          <IconButton
            icon={delIcon}
            altText="Удалить"
            onClick={() => openDeleteModal(event)}
          />
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openEditModal(event)}
          />
        </EditAndDeleteContainer>
      ) : null,
    };
  });

  if (recentlyRestored !== null) {
    const idx = events.findIndex((ev) => ev.id === recentlyRestored);
    if (idx !== -1) {
      rows.splice(idx + 1, 0, {
        description: (
          <RestoredRow
            colSpan={headers.length}
            message="Событие успешно восстановлено"
          />
        ),
      });
    }
  }
  if (recentlyCreated !== null) {
    const idx = events.findIndex((ev) => ev.id === recentlyCreated);
    if (idx !== -1) {
      rows.splice(idx + 1, 0, {
        description: (
          <RestoredRow
            colSpan={headers.length}
            message="Событие успешно создано"
          />
        ),
      });
    }
  }
  if (recentlyEdited !== null) {
    const idx = events.findIndex((ev) => ev.id === recentlyEdited);
    if (idx !== -1) {
      rows.splice(idx + 1, 0, {
        description: (
          <RestoredRow
            colSpan={headers.length}
            message="Событие успешно отредактировано"
          />
        ),
      });
    }
  }

  return (
    <EventsPageContainer>
      <Toast ref={toast} />
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <TitleWrapper>
            <Title>Календарь</Title>
            {hasActiveSubscription && (
              <ButtonWrapper>
                <Button
                  onClick={openCreateModal}
                  type="button"
                  variant="active"
                  width={`${pxToRem(221)}`}
                  height={`${pxToRem(50)}`}
                >
                  Добавить событие
                </Button>
              </ButtonWrapper>
            )}
          </TitleWrapper>
          <StatsWrapper>
            <TextDescription>Всего событий:</TextDescription>
            <TotalEvents>{eventCount}</TotalEvents>
          </StatsWrapper>
        </HeaderContainer>
        <Table
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={[
            { label: "5 событий", value: 5 },
            { label: "15 событий", value: 15 },
            { label: "20 событий", value: 20 },
          ]}
          minHeight="230px"
          loading={loading}
          specialRowKey="description"
          onSort={handleSort}
          remHeight={234}
        />
      </ContentWrapper>

      {isCreateModalOpen && (
        <CreateModal
          title="Добавление события"
          fields={[
            {
              name: "course",
              type: "selectWithInput",
              placeholder: "Курс",
              options: courseOptions,
              required: true,
            },
            {
              name: "calendar_day",
              type: "date",
              placeholder: "Дата",
              required: true,
            },
            {
              name: "description",
              type: "textarea",
              placeholder: "Описание",
              required: true,
            },
            {
              name: "time",
              type: "time",
              placeholder: "Время",
              required: true,
            },
          ]}
          onSubmit={handleCreateEvent}
          submitButtonText="Сохранить"
          onCancel={closeCreateModal}
          hasDragAndDrop={false}
          initialValues={{ time: "00:00" }}
          externalErrors={externalErrors}
        />
      )}

      {isEditModalOpen && editingEvent && (
        <CreateModal
          title="Редактирование события"
          fields={[
            {
              name: "course",
              type: "selectWithInput",
              placeholder: "Курс",
              options: courseOptions,
              required: true,
            },
            {
              name: "calendar_day",
              type: "date",
              placeholder: "Дата",
              required: true,
            },
            {
              name: "description",
              type: "textarea",
              placeholder: "Описание",
              required: true,
            },
            {
              name: "time",
              type: "time",
              placeholder: "Время",
              required: true,
            },
          ]}
          onSubmit={handleEditEvent}
          submitButtonText="Сохранить"
          onCancel={closeEditModal}
          initialValues={{
            course: String(editingEvent.course),
            calendar_day: editingEvent.calendar_day,
            description: editingEvent.description,
            time: editingEvent.time,
          }}
          hasDragAndDrop={false}
          externalErrors={externalErrors}
        />
      )}

      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить событие "${eventToDelete?.description}"?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}
    </EventsPageContainer>
  );
};

export default EventsPage;
